import { apiIsomorphicFetchInvite } from '#/api/invite/apiFetchInvite';
import { apiIsomorphicFetchUserEnvironment } from '#/api/userEnvironment/apiFetchUserEnvironment';
import { RouteModule } from '#/horizon/components/RouteModule';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { LayoutHandleProps } from '#/layouts/Layout/LayoutHandleProps';
import {
  authLoginRoute,
  authSignupRoute,
  type authUserInviteRoute,
} from '#/modules/auth/routing/auth.routes';
import { InvalidLinkPageLoadable } from '#/pages/InvalidLink/InvalidLinkPageLoadable';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import { idealistRedirect } from '#/routing/utils/idealistRedirect';

import { AuthUserInvitePageLoadable } from './AuthUserInvitePageLoadable';

export const handle = {
  variant: 'base',
} satisfies LayoutHandleProps;

export async function loader({
  request,
  context,
  params: { inviteId },
}: HorizonAppRouteLoaderArgs<typeof authUserInviteRoute>) {
  const [{ user }, inviteData] = await Promise.all([
    apiIsomorphicFetchUserEnvironment({
      request,
      context,
    })(),
    apiIsomorphicFetchInvite({ request, context })(inviteId).catch(() => null),
    AuthUserInvitePageLoadable.load(),
  ]);

  // Show an invalid link page if the invite has been used or expired
  if (!inviteData) return { invite: null };

  if (!user) {
    return idealistRedirect({ request, context })(
      inviteData.userExists
        ? authLoginRoute.with({ invite: inviteId })
        : authSignupRoute.with({ invite: inviteId }),
    );
  }

  return { invite: inviteData.invite };
}

export default function AuthUserInviteHorizonRoute({
  loaderData: { invite },
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { i18n } = useI18n();

  if (!invite) return <InvalidLinkPageLoadable />;

  return (
    <RouteModule title={i18n('Invitation')}>
      <AuthUserInvitePageLoadable invite={invite} />
    </RouteModule>
  );
}
